import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import type { PaletteMode } from '@mui/material';
import { getTheme } from './index';
import { PALETTE_MODE } from './theme.constants';

interface ThemeContextValue {
    mode: PaletteMode;
    toggleColorMode: () => void;
    isDark: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [mode, setMode] = useState<PaletteMode>(PALETTE_MODE.DARK);

    const toggleColorMode = useCallback(() => {
        setMode((prev) => (prev === PALETTE_MODE.DARK ? PALETTE_MODE.LIGHT : PALETTE_MODE.DARK));
    }, []);

    const theme = useMemo(() => getTheme(mode), [mode]);

    const contextValue = useMemo<ThemeContextValue>(
        () => ({
            mode,
            toggleColorMode,
            isDark: mode === PALETTE_MODE.DARK,
        }),
        [mode, toggleColorMode],
    );

    return (
        <ThemeContext value={contextValue}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext>
    );
}

export function useTheme(): ThemeContextValue {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}
